import { registerSW } from "virtual:pwa-register";
import { api } from "./api";

let updateSW: ((reload?: boolean) => Promise<void>) | null = null;
const listeners = new Set<() => void>();

/** Enregistre le service worker ; `onNeedRefresh` affiche le bandeau de mise à jour. */
export function initPwa() {
  if (!("serviceWorker" in navigator)) return;
  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      for (const l of listeners) l();
    },
  });
}

export function onUpdateAvailable(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function applyUpdate() {
  void updateSW?.(true);
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** Active les notifications push sur cet appareil (iOS : app installée sur l'écran d'accueil). */
export async function enablePush(): Promise<"granted" | "denied" | "unsupported" | "unavailable"> {
  if (!pushSupported()) return "unsupported";
  const { publicKey } = await api.get<{ publicKey: string | null }>("/push/public-key");
  if (!publicKey) return "unavailable";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource }));
  await api.post("/push/subscriptions", sub.toJSON());
  return "granted";
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.del("/push/subscriptions", { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe();
  }
}
